import { breakpoints } from '../../../../lib/detect';
import { getCurrentTime } from '../../../../lib/user-timing';
import { breakpointNameToAttribute } from './breakpoint-name-to-attribute';
import { defineSlot } from './define-slot';
/** A breakpoint can have various sizes assigned to it. You can assign either on
 * set of sizes or multiple.
 *
 * One size       - `data-mobile="300,50"`
 * Multiple sizes - `data-mobile="300,50|320,50"`
 */
const createSizeMapping = (attr) => attr
    .split('|')
    .map((size) => size === 'fluid' ? 'fluid' : size.split(',').map(Number));
const getAdBreakpointSizes = (advertNode) => breakpoints.reduce((sizes, breakpoint) => {
    const data = advertNode.getAttribute(`data-${breakpointNameToAttribute(breakpoint.name)}`);
    if (data) {
        sizes[breakpoint.name] = createSizeMapping(data);
    }
    return sizes;
}, {});
class Advert {
    constructor(adSlotNode) {
        this.size = null;
        this.isEmpty = null;
        this.isLoading = false;
        this.isRendering = false;
        this.isLoaded = false;
        this.isRendered = false;
        this.shouldRefresh = false;
        this.maxViewPercentage = 0;
        this.whenLoadedResolver = null;
        this.whenRenderedResolver = null;
        this.extraNodeClasses = [];
        this.timings = {
            createTime: null,
            startLoading: null,
            stopLoading: null,
            startRendering: null,
            stopRendering: null,
            loadingMethod: null,
            lazyWaitComplete: null,
        };
        this.hasPrebidSize = false;
        const sizes = getAdBreakpointSizes(adSlotNode);
        const slotDefinition = defineSlot(adSlotNode, sizes);
        this.id = adSlotNode.id;
        this.node = adSlotNode;
        this.sizes = sizes;
        this.slot = slotDefinition.slot;
        this.whenSlotReady = slotDefinition.slotReady;
        this.whenLoaded = new Promise((resolve) => {
            this.whenLoadedResolver = resolve;
        }).then((isLoaded) => {
            this.isLoaded = isLoaded;
            return isLoaded;
        });
        this.whenRendered = new Promise((resolve) => {
            this.whenRenderedResolver = resolve;
        }).then((isRendered) => {
            this.isRendered = isRendered;
            return isRendered;
        });
    }
    startLoading() {
        this.isLoading = true;
        this.timings.startLoading = getCurrentTime();
    }
    stopLoading(isLoaded) {
        this.isLoading = false;
        if (this.whenLoadedResolver) {
            this.whenLoadedResolver(isLoaded);
        }
        this.timings.stopLoading = getCurrentTime();
    }
    startRendering() {
        this.isRendering = true;
        this.timings.startRendering = getCurrentTime();
    }
    stopRendering(isRendered) {
        this.isRendering = false;
        if (this.whenRenderedResolver) {
            this.whenRenderedResolver(isRendered);
        }
    }
    updateExtraSlotClasses(...newClasses) {
        const classesToRemove = Advert.filterClasses(this.extraNodeClasses, newClasses);
        // IE11 does not support multiple arguments to classList.add/remove so do these one-by-ones
        classesToRemove.forEach((cls) => this.node.classList.remove(cls));
        newClasses.forEach((cls) => this.node.classList.add(cls));
        this.extraNodeClasses = newClasses;
    }
}
Advert.filterClasses = (oldClasses, newClasses) => oldClasses.filter((oldClass) => !newClasses.includes(oldClass));
export { Advert };
export const _ = {
    filterClasses: Advert.filterClasses,
};
